import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PillButton } from '@ab-platform/ui/pill-button';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { PhoneFieldRow } from '@/components/PhoneFieldRow';
import {
  STEP_FIELDS,
  type RegistrationFormValues,
} from '@/hooks/useRegistrationForm';

/**
 * Step 2 — Contatti organizzazione.
 *
 * Required fields by legalNature (PR-E):
 *   COMPANY → email, PEC, telefono fisso. Mobile dell'organizzazione
 *             OPZIONALE (il cellulare del firmatario sta sull'AdminPage).
 *   PERSON  → email, PEC, mobile (= cellulare della persona).
 *             Telefono fisso opzionale.
 *
 * Phone numbers carry the dial-prefix country (PhoneCountryPicker) plus
 * the local part. The submit mapper concats them for the BE wire.
 *
 * No verification flow here — PR-E removed the "Da verificare" badges.
 * Verification (link / OTP) is owned by a separate future track.
 *
 * Org email auto-prefills from `adminUser.email` on first visit if
 * blank. Subsequent visits don't overwrite a value the user typed.
 */
export function ContattiPage(): JSX.Element {
  const navigate = useNavigate();
  const form = useFormContext<RegistrationFormValues>();
  const { register, control, formState, getValues, setValue, trigger } = form;
  const errors = formState.errors.orgContacts;

  const legalNature = useWatch({ control, name: 'organization.legalNature' });
  const isPerson = legalNature === 'PERSON';

  useEffect(() => {
    const current = getValues('orgContacts.email');
    if (current && current.trim().length > 0) return;
    const adminEmail = getValues('adminUser.email');
    if (adminEmail && adminEmail.trim().length > 0) {
      setValue('orgContacts.email', adminEmail, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: one-shot mount prefill, no dependency tracking
  }, []);

  const handleContinue = async (): Promise<void> => {
    const ok = await trigger(
      STEP_FIELDS.contacts as unknown as Parameters<typeof trigger>[0],
    );
    if (ok) navigate('/registrazione-intermediario/indirizzi');
  };

  return (
    <FocusedPageShell
      title="Come possiamo contattarti?"
      description={
        isPerson
          ? 'Email e PEC sono obbligatorie. Telefono fisso e cellulare dell\'organizzazione sono opzionali (il cellulare della persona lo chiediamo allo step Amministratore).'
          : 'Email, PEC e telefono fisso sono obbligatori. Il cellulare dell\'organizzazione è opzionale (il cellulare del firmatario lo chiediamo allo step Amministratore).'
      }
      onBack={() => navigate('/registrazione-intermediario/organizzazione')}
      footer={
        <PillButton
          type="button"
          size="lg"
          onClick={handleContinue}
          className="w-full"
        >
          Continua
        </PillButton>
      }
    >
      <FloatingLabelInput
        label="Email"
        type="email"
        autoComplete="email"
        error={errors?.email?.message}
        {...register('orgContacts.email')}
      />

      <FloatingLabelInput
        label="PEC"
        type="email"
        autoComplete="off"
        error={errors?.pec?.message}
        {...register('orgContacts.pec')}
      />

      <PhoneFieldRow
        path="orgContacts.phone"
        label={
          isPerson ? 'Telefono fisso (opzionale)' : 'Telefono fisso'
        }
      />

      <PhoneFieldRow
        path="orgContacts.mobile"
        label={
          isPerson
            ? 'Cellulare organizzazione (opzionale)'
            : 'Cellulare organizzazione (opzionale)'
        }
      />
    </FocusedPageShell>
  );
}
